/**
 * Entity → display metadata mapping for AI search result cards.
 * Maps entity names (as returned by BTNet-Search API in snake_case)
 * to display labels and Blueprint icon components.
 *
 * Icon assignments follow the idealized Bp→Icon mapping (icon-entity-mapping.csv).
 */
import type { ReactNode } from "react";

import {
    IconAllowances,
    IconBids,
    IconBills,
    IconChangeOrders,
    IconClientUpdates,
    IconComments,
    IconCostCodes,
    IconCreditMemos,
    IconDailyLogs,
    IconDocuments,
    IconEstimate,
    IconInternalUsers,
    IconJobs,
    IconLeadOpportunities,
    IconLeadProposals,
    IconLienWaivers,
    IconMessages,
    IconOwnerInvoices,
    IconPeople,
    IconPhotos,
    IconPurchaseOrders,
    IconReceipts,
    IconRfIs,
    IconSchedule,
    IconSelections,
    IconSpecifications,
    IconStamp,
    IconSubsVendors,
    IconSurveys,
    IconTimeClock,
    IconToDos,
    IconVideos,
    IconWarranty,
} from "@btnet/blueprint-icons";

export interface IEntityMeta {
    label: string;
    icon: ReactNode;
}

/**
 * Keys are normalized entity names: lowercased with hyphens and underscores stripped.
 * The API returns snake_case (e.g. "daily_log") which normalizes to "dailylog".
 */
const META: Record<string, IEntityMeta> = {
    // ── Jobs & Leads ────────────────────────────────────────────────────
    job: { label: "Job", icon: <IconJobs /> },
    jobsite: { label: "Job", icon: <IconJobs /> },
    lead: { label: "Lead", icon: <IconLeadOpportunities /> },
    leadactivity: { label: "Lead Activity", icon: <IconLeadOpportunities /> },

    // ── People ──────────────────────────────────────────────────────────
    contact: { label: "Contact", icon: <IconPeople /> },
    sub: { label: "Sub / Vendor", icon: <IconSubsVendors /> },
    internaluser: { label: "Internal User", icon: <IconInternalUsers /> },

    // ── Scheduling ──────────────────────────────────────────────────────
    schedule: { label: "Schedule", icon: <IconSchedule /> },
    scheduleitem: { label: "Schedule", icon: <IconSchedule /> },
    phase: { label: "Phase", icon: <IconSchedule /> },

    // ── Tasks ───────────────────────────────────────────────────────────
    task: { label: "Task", icon: <IconToDos /> },

    // ── Selections ──────────────────────────────────────────────────────
    selection: { label: "Selection", icon: <IconSelections /> },
    selectionchoice: { label: "Selection Choice", icon: <IconSelections /> },
    selectionoption: { label: "Selection", icon: <IconSelections /> },

    // ── Financial ───────────────────────────────────────────────────────
    purchaseorder: { label: "Purchase Order", icon: <IconPurchaseOrders /> },
    changeorder: { label: "Change Order", icon: <IconChangeOrders /> },
    bill: { label: "Bill", icon: <IconBills /> },
    customerinvoice: { label: "Client Invoice", icon: <IconOwnerInvoices /> },
    allowance: { label: "Allowance", icon: <IconAllowances /> },
    costcode: { label: "Cost Code", icon: <IconCostCodes /> },
    costitem: { label: "Cost Item", icon: <IconCostCodes /> },
    deposit: { label: "Deposit", icon: <IconOwnerInvoices /> },
    creditmemo: { label: "Credit Memo", icon: <IconCreditMemos /> },
    estimate: { label: "Estimate", icon: <IconEstimate /> },
    lienwaiver: { label: "Lien Waiver", icon: <IconLienWaivers /> },
    receipt: { label: "Receipt", icon: <IconReceipts /> },

    // ── Bids ────────────────────────────────────────────────────────────
    bid: { label: "Bid", icon: <IconBids /> },
    bidpackage: { label: "Bid Package", icon: <IconBids /> },

    // ── Daily Logs & Warranty ───────────────────────────────────────────
    dailylog: { label: "Daily Log", icon: <IconDailyLogs /> },
    warranty: { label: "Warranty", icon: <IconWarranty /> },
    warrantyservice: { label: "Warranty Service", icon: <IconWarranty /> },

    // ── Media & Documents ───────────────────────────────────────────────
    document: { label: "Document", icon: <IconDocuments /> },
    photo: { label: "Photo", icon: <IconPhotos /> },
    video: { label: "Video", icon: <IconVideos /> },

    // ── Communication ───────────────────────────────────────────────────
    email: { label: "Message", icon: <IconMessages /> },
    emailmessage: { label: "Message", icon: <IconMessages /> },
    message: { label: "Message", icon: <IconMessages /> },
    channel: { label: "Channel", icon: <IconMessages /> },
    comment: { label: "Comment", icon: <IconComments /> },
    clientupdate: { label: "Client Update", icon: <IconClientUpdates /> },

    // ── Proposals ───────────────────────────────────────────────────────
    proposal: { label: "Proposal", icon: <IconLeadProposals /> },
    jobproposal: { label: "Proposal", icon: <IconLeadProposals /> },
    leadproposal: { label: "Proposal", icon: <IconLeadProposals /> },

    // ── RFIs & Submittals ───────────────────────────────────────────────
    rfi: { label: "RFI", icon: <IconRfIs /> },
    submittal: { label: "Submittal", icon: <IconStamp /> },
    specification: { label: "Specification", icon: <IconSpecifications /> },

    // ── Other ───────────────────────────────────────────────────────────
    survey: { label: "Survey", icon: <IconSurveys /> },
    timeclockentry: { label: "Time Clock Entry", icon: <IconTimeClock /> },
};

const DEFAULT_META: IEntityMeta = {
    label: "Item",
    icon: <IconJobs />,
};

/**
 * Normalize an entity name to a META lookup key.
 * The API returns snake_case (e.g. "daily_log"); strip separators to match META keys.
 */
function normalizeKey(entity: string): string {
    return entity.toLowerCase().replace(/[-_]/g, "");
}

/**
 * Stable identifier for a search hit used as the React `key` prop,
 * the `pendingKey` state value, and comparison target on the result card.
 */
function getHitKey(hit: { entity: string; id: string }): string {
    return `${hit.entity}:${hit.id}`;
}

export { getHitKey, normalizeKey };

export function getEntityMeta(entity: string): IEntityMeta {
    const key = normalizeKey(entity);
    if (META[key]) return META[key];

    // Generate a reasonable label from the raw entity name for unmapped entities
    const label = entity
        .toLowerCase()
        .replace(/[-_]/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (s) => s.toUpperCase());

    return { ...DEFAULT_META, label };
}
